import { useState, useEffect, useMemo } from 'react';
import { useFinance } from '@/contexts/FinanceContext';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { CounterpartyCombobox } from '@/features/counterparties/CounterpartyCombobox';
import { ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format, addDays, addWeeks, addMonths, addYears } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, TrendingUp, TrendingDown, ArrowLeftRight } from 'lucide-react';
import type {
  TransactionType,
  TransactionStatus,
  RecurrenceType,
  FinanceTransaction,
  RecurrenceActionScope,
} from '@/types/finance';

const RECURRENCE_OPTIONS: { value: RecurrenceType; label: string }[] = [
  { value: 'none', label: 'Não repetir' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'yearly', label: 'Anual' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quinzenal' },
  { value: 'daily', label: 'Diária' },
];

const getNextDate = (date: Date, recurrence: RecurrenceType): Date => {
  switch (recurrence) {
    case 'daily':
      return addDays(date, 1);
    case 'weekly':
      return addWeeks(date, 1);
    case 'biweekly':
      return addWeeks(date, 2);
    case 'monthly':
      return addMonths(date, 1);
    case 'yearly':
      return addYears(date, 1);
    default:
      return date;
  }
};

const getRecurrenceLabel = (r: RecurrenceType) =>
  ({
    daily: 'diárias',
    weekly: 'semanais',
    biweekly: 'quinzenais',
    monthly: 'mensais',
    yearly: 'anuais',
    none: '',
  })[r];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editTransaction?: FinanceTransaction;
  editScope?: RecurrenceActionScope;
}

export function TransactionModal({ open, onOpenChange, editTransaction, editScope }: Props) {
  const {
    categories,
    accounts,
    costCenters,
    addTransaction,
    updateTransaction,
    updateRecurringTransaction,
  } = useFinance();

  const isBatchEdit = editTransaction && (editScope === 'future' || editScope === 'all');
  const isEditingRecurring =
    editTransaction && editTransaction.id && editTransaction.recurrenceGroupId;

  const [type, setType] = useState<TransactionType>(editTransaction?.type ?? 'expense');
  const [description, setDescription] = useState(editTransaction?.description ?? '');
  const [amount, setAmount] = useState(editTransaction?.amount?.toString() ?? '');
  const [dueDate, setDueDate] = useState<Date | undefined>(editTransaction?.dueDate ?? new Date());
  const [paymentDate, setPaymentDate] = useState<Date | undefined>(editTransaction?.paymentDate);
  const [categoryId, setCategoryId] = useState(editTransaction?.categoryId ?? '');
  // Split de categorias: lista local com {optionId, amount} editáveis.
  // Quando length > 0, transação usa split (categoryId fica vazio).
  const initialSplits = editTransaction?.categorySplits ?? [];
  const [splitMode, setSplitMode] = useState(initialSplits.length > 0);
  const [splits, setSplits] = useState<SplitRow[]>(
    initialSplits.length > 0
      ? initialSplits.map((s) => ({
          optionId: s.categoryId,
          amount: s.amount.toFixed(2).replace('.', ','),
        }))
      : [
          { optionId: '', amount: '' },
          { optionId: '', amount: '' },
        ],
  );
  // Split de centros de custo: eixo independente do split de categorias.
  // Quando length > 0, transação usa split (costCenterId fica vazio).
  const initialCcSplits = editTransaction?.costCenterSplits ?? [];
  const [ccSplitMode, setCcSplitMode] = useState(initialCcSplits.length > 0);
  const [ccSplits, setCcSplits] = useState<SplitRow[]>(
    initialCcSplits.length > 0
      ? initialCcSplits.map((s) => ({
          optionId: s.costCenterId,
          amount: s.amount.toFixed(2).replace('.', ','),
        }))
      : [
          { optionId: '', amount: '' },
          { optionId: '', amount: '' },
        ],
  );
  const [accountId, setAccountId] = useState(editTransaction?.accountId ?? accounts[0]?.id ?? '');
  const [destinationAccountId, setDestinationAccountId] = useState(
    editTransaction?.destinationAccountId ?? '',
  );
  const [costCenterId, setCostCenterId] = useState(editTransaction?.costCenterId ?? '');
  const [counterpartyId, setCounterpartyId] = useState<string | undefined>(
    editTransaction?.counterpartyId,
  );
  const [interest, setInterest] = useState(
    editTransaction?.interest ? editTransaction.interest.toFixed(2).replace('.', ',') : '',
  );
  const [fine, setFine] = useState(
    editTransaction?.fine ? editTransaction.fine.toFixed(2).replace('.', ',') : '',
  );
  const [recurrence, setRecurrence] = useState<RecurrenceType>(
    editTransaction?.recurrence ?? 'none',
  );
  const [repetitions, setRepetitions] = useState(editTransaction?.numberOfRepetitions ?? 2);
  const [notes, setNotes] = useState(editTransaction?.notes ?? '');
  const [requiresApproval, setRequiresApproval] = useState(
    editTransaction?.approvalStatus === 'awaiting' ||
      editTransaction?.approvalStatus === 'approved' ||
      editTransaction?.approvalStatus === 'rejected',
  );

  const { client } = useCurrentClient();
  const autoApprovalLimit = Number(client?.auto_approval_limit ?? 0);

  const filteredCategories = categories.filter((c) => c.type === type);
  const activeCostCenters = costCenters.filter((cc) => cc.isActive);

  const previewDates = useMemo(() => {
    if (recurrence === 'none' || !dueDate || repetitions < 2) return [];
    const dates: Date[] = [];
    let d = dueDate;
    for (let i = 0; i < repetitions; i++) {
      dates.push(d);
      d = getNextDate(d, recurrence);
    }
    return dates;
  }, [dueDate, recurrence, repetitions]);

  const resetForm = () => {
    setType('expense');
    setDescription('');
    setAmount('');
    setDueDate(new Date());
    setPaymentDate(undefined);
    setCategoryId('');
    setDestinationAccountId('');
    setCostCenterId('');
    setCounterpartyId(undefined);
    setInterest('');
    setFine('');
    setRecurrence('none');
    setRepetitions(2);
    setNotes('');
    setRequiresApproval(false);
    setSplitMode(false);
    setSplits([
      { optionId: '', amount: '' },
      { optionId: '', amount: '' },
    ]);
    setCcSplitMode(false);
    setCcSplits([
      { optionId: '', amount: '' },
      { optionId: '', amount: '' },
    ]);
  };

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (open && editTransaction) {
      setType(editTransaction.type);
      setDescription(editTransaction.description);
      setAmount(editTransaction.amount.toString());
      setDueDate(editTransaction.dueDate);
      setPaymentDate(editTransaction.paymentDate);
      setCategoryId(editTransaction.categoryId);
      const eSplits = editTransaction.categorySplits ?? [];
      setSplitMode(eSplits.length > 0);
      setSplits(
        eSplits.length > 0
          ? eSplits.map((s) => ({
              optionId: s.categoryId,
              amount: s.amount.toFixed(2).replace('.', ','),
            }))
          : [
              { optionId: '', amount: '' },
              { optionId: '', amount: '' },
            ],
      );
      const eCcSplits = editTransaction.costCenterSplits ?? [];
      setCcSplitMode(eCcSplits.length > 0);
      setCcSplits(
        eCcSplits.length > 0
          ? eCcSplits.map((s) => ({
              optionId: s.costCenterId,
              amount: s.amount.toFixed(2).replace('.', ','),
            }))
          : [
              { optionId: '', amount: '' },
              { optionId: '', amount: '' },
            ],
      );
      setAccountId(editTransaction.accountId);
      setDestinationAccountId(editTransaction.destinationAccountId ?? '');
      setCostCenterId(editTransaction.costCenterId ?? '');
      setCounterpartyId(editTransaction.counterpartyId);
      setInterest(
        editTransaction.interest ? editTransaction.interest.toFixed(2).replace('.', ',') : '',
      );
      setFine(editTransaction.fine ? editTransaction.fine.toFixed(2).replace('.', ',') : '');
      setRecurrence(editTransaction.recurrence);
      setRepetitions(editTransaction.numberOfRepetitions ?? 2);
      setNotes(editTransaction.notes ?? '');
      setRequiresApproval(
        editTransaction.approvalStatus === 'awaiting' ||
          editTransaction.approvalStatus === 'approved' ||
          editTransaction.approvalStatus === 'rejected',
      );
    } else if (open && !editTransaction) {
      resetForm();
    }
  }, [open, editTransaction]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description || !amount || !dueDate || !accountId) return;
    if (type === 'transfer' && !destinationAccountId) return;

    // Validação categoria/split — só aplica para income/expense
    const numericAmountForSplit = parseFloat(amount.replace(',', '.'));
    let categorySplitsPayload: { categoryId: string; amount: number; orderIndex: number }[] | undefined;
    if (type !== 'transfer') {
      if (splitMode) {
        // Filtra splits válidos
        const validSplits = splits
          .map((s, idx) => ({
            categoryId: s.optionId,
            amount: parseFloat((s.amount || '').replace(/\./g, '').replace(',', '.')) || 0,
            orderIndex: idx,
          }))
          .filter((s) => s.categoryId && s.amount > 0);
        if (validSplits.length < 2) return;
        const sum = validSplits.reduce((a, b) => a + b.amount, 0);
        if (Math.abs(sum - numericAmountForSplit) > 0.01) return;
        categorySplitsPayload = validSplits;
      } else if (!categoryId) {
        return;
      }
    }

    // Validação split de centro de custo — mesmas regras do split de categoria,
    // exceto que centro de custo continua opcional quando o split está desligado.
    let costCenterSplitsPayload:
      | { costCenterId: string; amount: number; orderIndex: number }[]
      | undefined;
    if (type !== 'transfer' && ccSplitMode) {
      const validCcSplits = ccSplits
        .map((s, idx) => ({
          costCenterId: s.optionId,
          amount: parseFloat((s.amount || '').replace(/\./g, '').replace(',', '.')) || 0,
          orderIndex: idx,
        }))
        .filter((s) => s.costCenterId && s.amount > 0);
      if (validCcSplits.length < 2) return;
      const ccSum = validCcSplits.reduce((a, b) => a + b.amount, 0);
      if (Math.abs(ccSum - numericAmountForSplit) > 0.01) return;
      costCenterSplitsPayload = validCcSplits;
    }

    const computedStatus: TransactionStatus = paymentDate
      ? 'completed'
      : dueDate < new Date()
        ? 'overdue'
        : 'pending';

    const numericAmount = parseFloat(amount.replace(',', '.'));

    // Aprovação só faz sentido em despesas pendentes não pagas.
    // Se valor ≤ limite automático do cliente, dispensa aprovação mesmo se flag marcada.
    const needsApproval =
      type === 'expense' &&
      computedStatus !== 'completed' &&
      requiresApproval &&
      (autoApprovalLimit <= 0 || numericAmount > autoApprovalLimit);

    const interestNum = interest ? parseFloat(interest.replace(/\./g, '').replace(',', '.')) || 0 : 0;
    const fineNum = fine ? parseFloat(fine.replace(/\./g, '').replace(',', '.')) || 0 : 0;
    const includeEncargos = !!paymentDate;

    const baseFields = {
      type,
      description,
      amount: numericAmount,
      dueDate,
      // Quando há split, categoryId vai vazio — a fonte da verdade são os splits.
      categoryId: type === 'transfer' ? '' : categorySplitsPayload ? '' : categoryId,
      accountId,
      recurrence,
      ...(paymentDate && { paymentDate }),
      ...(type === 'transfer' && destinationAccountId && { destinationAccountId }),
      // Com split de CC, o escalar não vai no payload (a mutation zera cost_center_id).
      ...(costCenterId && !costCenterSplitsPayload && { costCenterId }),
      ...(counterpartyId && { counterpartyId }),
      ...(recurrence !== 'none' && { numberOfRepetitions: repetitions }),
      ...(notes && { notes }),
      // Encargos só relevantes em pagamentos. Para transações sem paymentDate,
      // forçamos 0 (mesmo se o usuário tinha digitado algo antes de remover a data).
      interest: includeEncargos ? interestNum : 0,
      fine: includeEncargos ? fineNum : 0,
      // Splits: presentes quando splitMode ativo; undefined caso contrário.
      // Em edição, mandar [] explícito quando o usuário desativou o split (para limpar splits antigos).
      ...(categorySplitsPayload
        ? { categorySplits: categorySplitsPayload }
        : editTransaction?.categorySplits && editTransaction.categorySplits.length > 0
          ? { categorySplits: [] as { categoryId: string; amount: number; orderIndex: number }[] }
          : {}),
      ...(costCenterSplitsPayload
        ? { costCenterSplits: costCenterSplitsPayload }
        : editTransaction?.costCenterSplits && editTransaction.costCenterSplits.length > 0
          ? {
              costCenterSplits: [] as { costCenterId: string; amount: number; orderIndex: number }[],
            }
          : {}),
    };

    const approvalField = needsApproval
      ? { approvalStatus: 'awaiting' as const }
      : { approvalStatus: 'not_required' as const };

    if (editTransaction && editTransaction.id) {
      const data = isBatchEdit
        ? { ...baseFields, ...approvalField }
        : { ...baseFields, status: computedStatus, ...approvalField };
      if (editScope && editTransaction.recurrenceGroupId) {
        updateRecurringTransaction(editTransaction.id, data, editScope, editTransaction);
      } else {
        updateTransaction(editTransaction.id, data);
      }
    } else {
      addTransaction({ ...baseFields, status: computedStatus, ...approvalField });
    }

    onOpenChange(false);
    resetForm();
  };

  const getSubmitVariant = () => {
    if (type === 'income') return 'income' as const;
    if (type === 'expense') return 'expense' as const;
    return 'default' as const;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">
            {editTransaction ? 'Editar Transação' : 'Nova Transação'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-5">
          {/* Type toggle */}
          <div
            className={cn('flex rounded-lg border bg-muted/50 p-1', isBatchEdit && 'opacity-60')}
          >
            {[
              { value: 'income' as const, Icon: TrendingUp, label: 'Receita' },
              { value: 'expense' as const, Icon: TrendingDown, label: 'Despesa' },
              { value: 'transfer' as const, Icon: ArrowLeftRight, label: 'Transferência' },
            ].map(({ value, Icon, label }) => (
              <Button
                key={value}
                type="button"
                variant={type === value ? (value === 'transfer' ? 'default' : value) : 'ghost'}
                className="flex-1 rounded-md"
                disabled={!!isBatchEdit}
                onClick={() => {
                  setType(value);
                  setCategoryId('');
                }}
              >
                <Icon className="mr-2 h-4 w-4" />
                {label}
              </Button>
            ))}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="tx-desc">Descrição *</Label>
            <Input
              id="tx-desc"
              placeholder={
                type === 'transfer' ? 'Ex: Aporte para investimentos' : 'Ex: Aluguel consultório'
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          {/* Amount */}
          <div className="space-y-1.5">
            <Label htmlFor="tx-amount">Valor (R$) *</Label>
            <Input
              id="tx-amount"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d,.]/g, ''))}
              required
            />
          </div>

          {/* Due date */}
          <div className="space-y-1.5">
            <Label>
              {type === 'transfer' ? 'Data da Transferência *' : 'Data de Vencimento *'}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !dueDate && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, 'dd/MM/yyyy', { locale: ptBR }) : 'Selecione uma data'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                  locale={ptBR}
                  captionLayout="dropdown"
                  fromYear={2020}
                  toYear={2035}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Payment date */}
          {type !== 'transfer' && (
            <div className="space-y-1.5">
              <Label>Data de Pagamento/Recebimento</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !paymentDate && 'text-muted-foreground',
                      isBatchEdit && 'cursor-not-allowed opacity-60',
                    )}
                    disabled={!!isBatchEdit}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {paymentDate
                      ? format(paymentDate, 'dd/MM/yyyy', { locale: ptBR })
                      : 'Não pago/recebido'}
                  </Button>
                </PopoverTrigger>
                {!isBatchEdit && (
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={paymentDate}
                      onSelect={setPaymentDate}
                      initialFocus
                      locale={ptBR}
                      captionLayout="dropdown"
                      fromYear={2020}
                      toYear={2035}
                    />
                  </PopoverContent>
                )}
              </Popover>
              {!isBatchEdit && paymentDate && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPaymentDate(undefined)}
                  className="text-xs text-muted-foreground"
                >
                  Limpar data de pagamento
                </Button>
              )}
            </div>
          )}

          {/* Multa e juros — só quando há paymentDate */}
          {type !== 'transfer' && paymentDate && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="tx-fine">Multa (R$)</Label>
                <Input
                  id="tx-fine"
                  placeholder="0,00"
                  value={fine}
                  onChange={(e) => setFine(e.target.value)}
                  inputMode="decimal"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tx-interest">Juros (R$)</Label>
                <Input
                  id="tx-interest"
                  placeholder="0,00"
                  value={interest}
                  onChange={(e) => setInterest(e.target.value)}
                  inputMode="decimal"
                />
              </div>
            </div>
          )}

          {/* Category — single ou split */}
          {type !== 'transfer' && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{splitMode ? 'Categorias (split) *' : 'Categoria *'}</Label>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={splitMode}
                    onChange={(e) => setSplitMode(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  Dividir em várias categorias
                </label>
              </div>
              {!splitMode ? (
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCategories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: c.color }}
                          />
                          {c.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <SplitEditor
                  splits={splits}
                  onChange={setSplits}
                  options={filteredCategories}
                  totalAmount={parseFloat((amount || '0').replace(',', '.')) || 0}
                  optionPlaceholder="Categoria"
                  addLabel="+ Adicionar categoria"
                />
              )}
            </div>
          )}

          {/* Source account */}
          <div className="space-y-1.5">
            <Label>{type === 'transfer' ? 'Conta de Origem *' : 'Conta *'}</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma conta" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: a.color }} />
                      {a.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Destination account */}
          {type === 'transfer' && (
            <div className="space-y-1.5">
              <Label>Conta de Destino *</Label>
              <Select value={destinationAccountId} onValueChange={setDestinationAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conta de destino" />
                </SelectTrigger>
                <SelectContent>
                  {accounts
                    .filter((a) => a.id !== accountId)
                    .map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: a.color }}
                          />
                          {a.name}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Cost center — single ou split */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>
                {ccSplitMode && type !== 'transfer' ? 'Centros de Custo (split)' : 'Centro de Custo'}
              </Label>
              {type !== 'transfer' && (
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={ccSplitMode}
                    onChange={(e) => setCcSplitMode(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  Dividir em vários centros de custo
                </label>
              )}
            </div>
            {!(ccSplitMode && type !== 'transfer') ? (
              <Select
                value={costCenterId || '__none__'}
                onValueChange={(v) => setCostCenterId(v === '__none__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um centro de custo (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nenhum</SelectItem>
                  {activeCostCenters.map((cc) => (
                    <SelectItem key={cc.id} value={cc.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: cc.color }}
                        />
                        {cc.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <SplitEditor
                splits={ccSplits}
                onChange={setCcSplits}
                options={activeCostCenters}
                totalAmount={parseFloat((amount || '0').replace(',', '.')) || 0}
                optionPlaceholder="Centro de custo"
                addLabel="+ Adicionar centro de custo"
              />
            )}
          </div>

          {/* Counterparty */}
          {type !== 'transfer' && (
            <div className="space-y-1.5">
              <Label>{type === 'income' ? 'Cliente' : 'Fornecedor'}</Label>
              <CounterpartyCombobox
                value={counterpartyId}
                onChange={setCounterpartyId}
                role={type === 'income' ? 'income' : 'expense'}
                placeholder={
                  type === 'income'
                    ? 'Selecione ou crie um cliente (opcional)'
                    : 'Selecione ou crie um fornecedor (opcional)'
                }
              />
            </div>
          )}

          {/* Recurrence */}
          <div className="space-y-1.5">
            <Label>Recorrência</Label>
            <Select
              value={recurrence}
              onValueChange={(v) => setRecurrence(v as RecurrenceType)}
              disabled={!!isEditingRecurring || !!isBatchEdit}
            >
              <SelectTrigger
                className={isEditingRecurring || isBatchEdit ? 'cursor-not-allowed opacity-60' : ''}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Repetitions */}
          {recurrence !== 'none' && (
            <div className="space-y-1.5">
              <Label>Número de Repetições</Label>
              <Input
                type="number"
                min={2}
                max={60}
                value={repetitions}
                onChange={(e) =>
                  setRepetitions(Math.min(60, Math.max(2, parseInt(e.target.value) || 2)))
                }
                disabled={!!isEditingRecurring}
                className={isEditingRecurring ? 'cursor-not-allowed opacity-60' : ''}
              />
              {!isEditingRecurring && (
                <>
                  <div className="flex gap-2">
                    {[3, 6, 12].map((n) => (
                      <Button
                        key={n}
                        type="button"
                        variant={repetitions === n ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        onClick={() => setRepetitions(n)}
                      >
                        {n}x
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Serão criadas {repetitions} transações {getRecurrenceLabel(recurrence)} (mín: 2,
                    máx: 60)
                  </p>
                  {previewDates.length > 0 && dueDate && (
                    <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
                      <p className="mb-2 font-medium">Transações que serão criadas:</p>
                      <ul className="max-h-24 space-y-1 overflow-y-auto">
                        {previewDates.slice(0, 5).map((d, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <span className="w-4 text-center">{i + 1}.</span>
                            <span>{format(d, 'dd/MM/yyyy', { locale: ptBR })}</span>
                          </li>
                        ))}
                        {previewDates.length > 5 && (
                          <li className="italic">... e mais {previewDates.length - 5}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="tx-notes">Observações</Label>
            <Textarea
              id="tx-notes"
              placeholder="Adicione observações (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Aprovação do cliente — apenas em despesas não pagas */}
          {type === 'expense' && !paymentDate && (
            <div className="space-y-1.5 rounded-lg border border-border bg-secondary/30 p-3">
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={requiresApproval}
                  onChange={(e) => setRequiresApproval(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded accent-primary"
                />
                <div className="flex-1">
                  <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                    <ShieldCheck size={14} className="text-primary" />
                    Exigir aprovação do cliente
                  </span>
                  <p className="text-xs text-muted-foreground">
                    O dentista precisa aprovar esse pagamento no portal antes da execução.
                  </p>
                  {requiresApproval && autoApprovalLimit > 0 && (
                    <p className="mt-1 text-xs text-amber-700">
                      Limite automático configurado:{' '}
                      <strong>
                        {autoApprovalLimit.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </strong>
                      . Valores abaixo desse limite não exigirão aprovação.
                    </p>
                  )}
                </div>
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" variant={getSubmitVariant()} className="flex-1">
              {editTransaction ? 'Salvar' : type === 'transfer' ? 'Transferir' : 'Adicionar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface SplitRow {
  optionId: string;
  amount: string;
}

interface SplitEditorProps {
  splits: SplitRow[];
  onChange: (splits: SplitRow[]) => void;
  /** Opções selecionáveis do eixo do split (categorias, centros de custo, ...). */
  options: Array<{ id: string; name: string; color: string }>;
  totalAmount: number;
  optionPlaceholder: string;
  addLabel: string;
}

function parseBrlInline(raw: string): number {
  if (!raw) return 0;
  const cleaned = raw.replace(/\./g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function fmtBrlInline(n: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

function SplitEditor({
  splits,
  onChange,
  options,
  totalAmount,
  optionPlaceholder,
  addLabel,
}: SplitEditorProps) {
  const splitsSum = splits.reduce((s, r) => s + parseBrlInline(r.amount), 0);
  const diff = totalAmount - splitsSum;
  const isValid = totalAmount > 0 && Math.abs(diff) <= 0.01;

  const updateRow = (idx: number, patch: Partial<SplitRow>) => {
    onChange(splits.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addRow = () => onChange([...splits, { optionId: '', amount: '' }]);
  const removeRow = (idx: number) =>
    onChange(splits.length > 2 ? splits.filter((_, i) => i !== idx) : splits);

  return (
    <div className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3">
      {splits.map((s, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <Select
            value={s.optionId}
            onValueChange={(v) => updateRow(idx, { optionId: v })}
          >
            <SelectTrigger className="h-9 flex-1">
              <SelectValue placeholder={optionPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                    {c.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            inputMode="decimal"
            placeholder="0,00"
            value={s.amount}
            onChange={(e) => updateRow(idx, { amount: e.target.value })}
            className="h-9 w-28 text-right"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-destructive"
            onClick={() => removeRow(idx)}
            disabled={splits.length <= 2}
            title="Remover linha"
          >
            ×
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addRow} className="w-full">
        {addLabel}
      </Button>
      <div
        className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${
          isValid
            ? 'border-income/30 bg-income/5 text-income'
            : 'border-expense/30 bg-expense/5 text-expense'
        }`}
      >
        <span>
          Soma: <strong>{fmtBrlInline(splitsSum)}</strong> / Total: {fmtBrlInline(totalAmount)}
        </span>
        <span>{isValid ? '✓ OK' : `Diferença ${fmtBrlInline(diff)}`}</span>
      </div>
    </div>
  );
}
